import { useMemo } from "react";
import {
  Card,
  Skeleton,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Typography,
} from "@altimateai/lego";
import { useWorkspacesCostTrendsByProduct } from "@/pages/workspaces/hooks";
import { useAppState } from "@/modules/app/useAppContext";
import useFiltersBySearchParams from "@/helpers/useFiltersBySearchParams";
import { useDeploymentTypeTrends, useEngineTypeTrends } from "../hooks";
import type { DeploymentTypeTrendsResponse, EngineTypeTrendsResponse } from "../types";
import StackedBarChart from "./StackedBarChart";

const WORKSPACE_TABS = [
  { value: "compute", label: "Compute Type Distribution" },
  { value: "serverless", label: "Serverless vs Provisioned" },
  { value: "photon", label: "Standard vs Photon" },
] as const;

type WorkspaceTabValue = (typeof WORKSPACE_TABS)[number]["value"];

const COMPUTE_PRODUCTS = new Set(["JOBS", "ALL_PURPOSE", "DLT", "INTERACTIVE"]);

const COMPUTE_TYPE_COLORS: Record<string, string> = {
  JOBS: "#4C9AFF",
  ALL_PURPOSE: "#FF754C",
  DLT: "#36B37E",
  INTERACTIVE: "#9B59B6",
};
const COMPUTE_TYPE_LABELS: Record<string, string> = {
  JOBS: "Jobs",
  ALL_PURPOSE: "All Purpose",
  DLT: "DLT",
  INTERACTIVE: "Interactive",
};

const DEPLOYMENT_TYPE_COLORS: Record<string, string> = {
  serverless: "#4C9AFF",
  provisioned: "#FF754C",
};
const DEPLOYMENT_TYPE_LABELS: Record<string, string> = {
  serverless: "Serverless",
  provisioned: "Provisioned",
};

const ENGINE_TYPE_COLORS: Record<string, string> = {
  photon: "#36B37E",
  standard: "#9B59B6",
};
const ENGINE_TYPE_LABELS: Record<string, string> = {
  photon: "Photon",
  standard: "Standard",
};

interface Props {
  startDate: string;
  endDate: string;
  workspaceId?: string;
}

/**
 * Pivot a /trends/by-X response into a wide-format array suitable for a
 * stacked recharts BarChart: one row per period, one column per type, the
 * cell value being that type's cost for that period.
 */
const pivotTrendsByType = <T extends { period: string; estimated_cost: number }>(
  trends: T[] | undefined,
  typeKey: keyof T
): Array<Record<string, number | string>> => {
  if (!trends?.length) return [];
  const periodMap = new Map<string, Record<string, number | string>>();
  for (const item of trends) {
    if (!periodMap.has(item.period)) {
      periodMap.set(item.period, { date: item.period });
    }
    const row = periodMap.get(item.period)!;
    const type = String(item[typeKey]);
    row[type] = Math.round((((row[type] as number) || 0) + item.estimated_cost) * 100) / 100;
  }
  return Array.from(periodMap.values()).sort((a, b) =>
    String(a.date).localeCompare(String(b.date))
  );
};

const WORKSPACE_TAB_VALUES = new Set<string>(WORKSPACE_TABS.map(tab => tab.value));

const WorkspaceCostChart = ({ startDate, endDate, workspaceId }: Props) => {
  const { currency } = useAppState();
  const { searchParams, updateSearchParamsByKey } = useFiltersBySearchParams();
  const workspaceTabParam = searchParams.get("workspaceTab");
  const activeTab: WorkspaceTabValue =
    workspaceTabParam && WORKSPACE_TAB_VALUES.has(workspaceTabParam)
      ? (workspaceTabParam as WorkspaceTabValue)
      : "compute";

  const trendsParams = {
    start_date: startDate,
    end_date: endDate,
    granularity: "day" as const,
    workspace_id: workspaceId,
  };
  const computeQuery = useWorkspacesCostTrendsByProduct(trendsParams, true);
  const deploymentQuery = useDeploymentTypeTrends(trendsParams);
  const engineQuery = useEngineTypeTrends(trendsParams);

  const isLoading = computeQuery.isLoading || deploymentQuery.isLoading || engineQuery.isLoading;
  const error = computeQuery.error || deploymentQuery.error || engineQuery.error;

  const computeChartData = useMemo(() => {
    const trends = computeQuery.data?.trends;
    if (!trends) return [];
    const periodMap = new Map<string, Record<string, number | string>>();
    for (const item of trends) {
      if (!COMPUTE_PRODUCTS.has(item.product)) continue;
      if (!periodMap.has(item.period)) {
        periodMap.set(item.period, { date: item.period });
      }
      const periodData = periodMap.get(item.period)!;
      const current = (periodData[item.product] as number) || 0;
      periodData[item.product] = Math.round((current + item.estimated_cost) * 100) / 100;
    }
    return Array.from(periodMap.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date))
    );
  }, [computeQuery.data]);

  const activeComputeProducts = useMemo(() => {
    const trends = computeQuery.data?.trends;
    if (!trends) return [];
    const seen = new Set<string>();
    for (const item of trends) {
      if (COMPUTE_PRODUCTS.has(item.product)) seen.add(item.product);
    }
    return ["JOBS", "ALL_PURPOSE", "DLT", "INTERACTIVE"].filter(p => seen.has(p));
  }, [computeQuery.data]);

  const deploymentChartData = useMemo(
    () =>
      pivotTrendsByType<DeploymentTypeTrendsResponse["trends"][number]>(
        deploymentQuery.data?.trends,
        "deployment_type"
      ),
    [deploymentQuery.data]
  );
  const deploymentTypes = useMemo(
    () =>
      ["serverless", "provisioned"].filter(t => deploymentQuery.data?.deployment_types.includes(t)),
    [deploymentQuery.data]
  );

  const engineChartData = useMemo(
    () =>
      pivotTrendsByType<EngineTypeTrendsResponse["trends"][number]>(
        engineQuery.data?.trends,
        "engine_type"
      ),
    [engineQuery.data]
  );
  const engineTypes = useMemo(
    () => ["photon", "standard"].filter(t => engineQuery.data?.engine_types.includes(t)),
    [engineQuery.data]
  );

  if (isLoading) {
    return (
      <Card className="al-p-4" style={{ borderColor: "#D5DBE4" }}>
        <Stack direction="column" gap="md">
          <Skeleton className="al-h-7 al-w-40" />
          <Skeleton className="al-h-8 al-w-96" />
          <Skeleton className="al-h-72 al-w-full" />
        </Stack>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="al-p-4" style={{ borderColor: "#D5DBE4" }}>
        <Typography className="al-text-red-500">Failed to load workspace cost trends</Typography>
      </Card>
    );
  }

  return (
    <Card className="al-p-4" style={{ borderColor: "#D5DBE4" }}>
      <Tabs
        variant="default"
        value={activeTab}
        onValueChange={(value: string) => updateSearchParamsByKey({ workspaceTab: value })}
      >
        <Typography variant="section-heading" className="al-mb-4">
          Workspace
        </Typography>

        <TabsList className="al-mb-4">
          {WORKSPACE_TABS.map(tab => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="compute">
          <StackedBarChart
            data={computeChartData}
            series={activeComputeProducts}
            colors={COMPUTE_TYPE_COLORS}
            labels={COMPUTE_TYPE_LABELS}
            currency={currency}
          />
        </TabsContent>

        <TabsContent value="serverless">
          <StackedBarChart
            data={deploymentChartData}
            series={deploymentTypes}
            colors={DEPLOYMENT_TYPE_COLORS}
            labels={DEPLOYMENT_TYPE_LABELS}
            currency={currency}
          />
        </TabsContent>

        <TabsContent value="photon">
          <StackedBarChart
            data={engineChartData}
            series={engineTypes}
            colors={ENGINE_TYPE_COLORS}
            labels={ENGINE_TYPE_LABELS}
            currency={currency}
          />
        </TabsContent>
      </Tabs>
    </Card>
  );
};

export default WorkspaceCostChart;
